import { useState } from 'react';
import { FetchMoreQueryOptions, ApolloQueryResult } from '@apollo/client';

interface PageInfo {
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  startCursor: string;
  endCursor: string;
}

type Direction = 'next' | 'previous';

export function usePagination<TData>({
  pageSize,
  data,
  fetchMore,
  getPageInfo,
  updateQuery,
}: {
  pageSize: number;
  data: TData | undefined;
  fetchMore: (options: any) => Promise<ApolloQueryResult<TData>>;
  getPageInfo: (data: TData) => PageInfo;
  updateQuery: (prev: TData, { fetchMoreResult }: { fetchMoreResult: TData | undefined }) => TData;
}) {
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const pageInfo = data ? getPageInfo(data) : null;

  const handlePageChange = (direction: Direction) => {
    if (!pageInfo) return;

    // Check if we can navigate in the requested direction
    if (direction === 'next' && !pageInfo.hasNextPage) return;
    if (direction === 'previous' && !pageInfo.hasPreviousPage) return;

    setIsLoadingMore(true);
    const variables: any =
      direction === 'next'
        ? {
            first: pageSize,
            after: pageInfo.endCursor,
          }
        : {
            first: null,
            last: pageSize,
            before: pageInfo.startCursor,
          };

    fetchMore({
      variables,
      updateQuery,
    }).finally(() => {
      setIsLoadingMore(false);
    });
  };

  return {
    pageSize,
    pageInfo,
    isLoadingMore,
    handlePageChange,
    canGoNext: pageInfo?.hasNextPage ?? false,
    canGoPrevious: pageInfo?.hasPreviousPage ?? false,
  };
} 
